import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useNavigationType, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MagnifyingGlassIcon, StarIcon } from '@heroicons/react/24/outline';
import DOMPurify from 'dompurify';
import Breadcrumb from '../components/layout/Breadcrumb';
import { savedHistoryScroll } from '../components/layout/Layout';
import RoomBar from '../components/layout/RoomBar';
import { PAGE_SIDEBAR_LAYOUT, PageSidebar } from '../components/layout/OnThisPage';
import { toolkitNavItems } from '../components/hubs/ToolkitSectionTree';
import InThisNav, { InThisNavItem } from '../components/programs/InThisNav';
import ProgramHubChapter from '../components/programs/ProgramHubChapter';
import ResourceCard from '../components/resources/ResourceCard';
import ResourceCardSkeleton from '../components/resources/ResourceCardSkeleton';
import ErrorState from '../components/ui/ErrorState';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import { usePageTitle } from '../hooks/usePageTitle';
import { useScrollSpy } from '../hooks/useScrollSpy';
import { programAPI } from '../services/programApi';
import Input from '../components/ui/Input';
import { useAccessLevel } from '../hooks/useAccessLevel';
import { wordpressAPI } from '../services/wordpressApi';
import { Program, ProgramHighlight, ProgramHub } from '../types/program';
import { WPMedia, WPPost } from '../types/wordpress';
import { decodeHtmlEntities } from '../utils/htmlEntities';
import { sectionAnchors } from '../utils/headingToc';
import { placePath } from '../utils/placePaths';
import { hubProseClassName } from '../utils/hubProse';
import {
  hubOutline,
  hubOutlineTree,
  parseProgramFocus,
  previewResources,
  programHubHref,
  pinScrollAnchor,
  hashScrollBehavior,
  withSmoothScroll,
} from '../utils/hubChapter';
import { filterDocumentAttachments } from '../utils/resourceAttachments';
import NestedSectionRail from '../components/hubs/NestedSectionRail';
import StickySectionHeading from '../components/hubs/StickySectionHeading';
import { stickySectionHeadingClass } from '../utils/stickySectionHeading';
import { pageFromTo, readPageFrom, reopenSearchState } from '../utils/pageFrom';
import {
  markProgramScanSeen,
  markProgramScanUnseen,
  programScanWasSeen,
  programVisitAlreadyStarted,
  readProgramScan,
  readFocusHubSlug,
  wantsProgramScanRestore,
} from '../utils/programScan';
import {
  beginPlaceTransition,
  closePlace,
  isPlainClick,
  markPlaceFocus,
  placeEnterClass,
  readPlaceSurface,
  usePlacePage,
  usePlaceSlide,
} from '../utils/placeMotion';

function hubForHighlight(hubs: ProgramHub[], postId: number) {
  return [...hubs]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title))
    .find((hub) => hub.resources.some((resource) => resource.wpPostId === postId));
}

function chapterHeading(hubSlug: string | null): HTMLElement | null {
  if (!hubSlug) return null;
  const heading = document.getElementById(`hub-${hubSlug}`)?.querySelector('h2');
  return heading instanceof HTMLElement ? heading : null;
}

function sectionHeading(sectionId: string): HTMLElement | null {
  const heading = document.getElementById(sectionId)?.querySelector('h2');
  return heading instanceof HTMLElement ? heading : null;
}

function wantedPostIds(
  program: Program,
  focus: { hubSlug: string | null; sectionId: string | null }
): number[] {
  const ids = new Set<number>();
  const entered = program.hubs.find((hub) => hub.slug === focus.hubSlug);
  if (entered) {
    for (const resource of entered.resources) ids.add(resource.wpPostId);
    return [...ids];
  }
  for (const highlight of program.highlights) ids.add(highlight.wpPostId);
  for (const hub of program.hubs) {
    for (const resource of previewResources(hub.resources)) ids.add(resource.wpPostId);
  }
  return [...ids];
}

type ProgramViewCache = {
  program: Program;
  posts: Map<number, WPPost>;
  attachments: Map<number, WPMedia[]>;
};

const programViewCache = new Map<string, ProgramViewCache>();

export default function ProgramDetail() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const seeded = slug ? programViewCache.get(slug) : undefined;
  const [program, setProgram] = useState<Program | null>(seeded?.program ?? null);
  const [resourcePosts, setResourcePosts] = useState<Map<number, WPPost>>(
    () => seeded?.posts ?? new Map()
  );
  const [attachmentsByPostId, setAttachmentsByPostId] = useState<Map<number, WPMedia[]>>(
    () => seeded?.attachments ?? new Map()
  );
  const [loading, setLoading] = useState(!seeded);
  const [postsReady, setPostsReady] = useState(Boolean(seeded));
  const [failedPostIds, setFailedPostIds] = useState<Set<number>>(() => new Set());
  const [pageFilter, setPageFilter] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const fetchedPostIds = useRef(new Set<number>(seeded?.posts.keys() ?? []));
  const fetchedAttachmentIds = useRef(new Set<number>(seeded?.attachments.keys() ?? []));
  const slide = usePlaceSlide();
  const pageRef = useRef<HTMLDivElement>(null);
  usePlacePage(pageRef);
  const entrance = readPageFrom(location.state);
  const openedSurface = readPlaceSurface(location.state);
  const listBackTo = entrance ? pageFromTo(entrance) : '/resources';
  const listBackLabel = entrance?.label ?? 'Back to Resources';
  const enteredHubForTitle = program?.hubs.find((hub) => {
    const id = location.hash.replace(/^#/, '');
    if (!id.startsWith('hub-')) return false;
    return hub.slug === parseProgramFocus(location.hash, program.hubs).hubSlug;
  });
  usePageTitle(enteredHubForTitle?.title ?? program?.title);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!slug) {
        setError(new Error('Invalid program slug'));
        setLoading(false);
        return;
      }

      const cached = programViewCache.get(slug);
      try {
        setError(null);
        if (cached) {
          setProgram(cached.program);
          setResourcePosts(cached.posts);
          setAttachmentsByPostId(cached.attachments);
          fetchedPostIds.current = new Set(cached.posts.keys());
          fetchedAttachmentIds.current = new Set(cached.attachments.keys());
          setPostsReady(true);
          setLoading(false);
        } else {
          setLoading(true);
          setPostsReady(false);
        }
        const data = await programAPI.fetchProgramBySlug(slug);
        if (cancelled) return;
        if (!data) {
          setError(new Error('Not found'));
          setProgram(null);
          setLoading(false);
          return;
        }
        if (!cached) {
          fetchedPostIds.current.clear();
          fetchedAttachmentIds.current.clear();
          setResourcePosts(new Map());
          setAttachmentsByPostId(new Map());
          setPostsReady(false);
        }
        setProgram(data);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to load program'));
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!program || program.slug !== slug) return;
    programViewCache.set(program.slug, {
      program,
      posts: resourcePosts,
      attachments: attachmentsByPostId,
    });
  }, [program, resourcePosts, attachmentsByPostId, slug]);

  const focus = useMemo(
    () => (program ? parseProgramFocus(location.hash, program.hubs) : { hubSlug: null, sectionId: null }),
    [program, location.hash]
  );
  const postIds = useMemo(
    () => (program ? wantedPostIds(program, focus) : []),
    [program, focus]
  );
  useEffect(() => {
    if (!program) return;
    const missing = postIds.filter((id) => !fetchedPostIds.current.has(id));
    if (missing.length === 0) {
      setPostsReady(true);
      return;
    }

    let cancelled = false;
    const loadPosts = async () => {
      setPostsReady(false);
      for (let index = 0; index < missing.length; index += 8) {
        const slice = missing.slice(index, index + 8);
        const entries = await Promise.all(
          slice.map(async (postId) => {
            try {
              return [postId, await wordpressAPI.fetchPost(postId)] as const;
            } catch (err) {
              console.warn(`Failed to fetch resource ${postId}:`, err);
              return [postId, null] as const;
            }
          })
        );
        if (cancelled) return;
        for (const [postId] of entries) fetchedPostIds.current.add(postId);
        setResourcePosts((prev) => {
          const next = new Map(prev);
          for (const [postId, post] of entries) {
            if (post) next.set(postId, post);
          }
          return next;
        });
        setFailedPostIds((prev) => {
          const next = new Set(prev);
          for (const [postId, post] of entries) {
            if (post) next.delete(postId);
            else next.add(postId);
          }
          return next;
        });
      }
      if (!cancelled) setPostsReady(true);
    };

    loadPosts();
    return () => {
      cancelled = true;
    };
  }, [program, postIds]);

  useEffect(() => {
    if (!program) return;
    const missing = postIds.filter((id) => !fetchedAttachmentIds.current.has(id));
    if (missing.length === 0) return;

    let cancelled = false;
    const loadAttachments = async () => {
      for (let index = 0; index < missing.length; index += 8) {
        if (cancelled) return;
        const slice = missing.slice(index, index + 8);
        const entries = await Promise.all(
          slice.map(async (postId) => {
            try {
              const media = await wordpressAPI.fetchAttachments(postId);
              return [postId, filterDocumentAttachments(media)] as const;
            } catch {
              return [postId, []] as const;
            }
          })
        );
        if (cancelled) return;
        for (const [postId] of entries) fetchedAttachmentIds.current.add(postId);
        setAttachmentsByPostId((prev) => {
          const next = new Map(prev);
          for (const [postId, attachments] of entries) next.set(postId, [...attachments]);
          return next;
        });
      }
    };

    loadAttachments();
    return () => {
      cancelled = true;
    };
  }, [program, postIds]);

  useEffect(() => {
    const id = location.hash.replace(/^#/, '');
    if (!id || !program) return;
    const saved = savedHistoryScroll(location.key);
    const historyRestore = navigationType === 'POP' && (
      (saved != null && saved > 0)
      || Boolean(location.state && typeof location.state === 'object' && (location.state as { restoreScroll?: boolean }).restoreScroll)
    );
    if (historyRestore) return;
    markPlaceFocus(sectionHeading(id));
    return pinScrollAnchor(id, hashScrollBehavior(location.state));
  }, [location.hash, location.key, location.state, navigationType, program]);

  useEffect(() => {
    if (!program || focus.hubSlug) return;
    markProgramScanSeen(program.slug);
  }, [program, focus.hubSlug]);

  const previousHub = useRef<string | null | undefined>(undefined);
  useLayoutEffect(() => {
    if (!slug || !program) return;
    const current = focus.hubSlug;
    const previous = previousHub.current;
    previousHub.current = current;
    if (previous === undefined) {
      const alreadyStarted = programVisitAlreadyStarted();
      const sectionId = location.hash.startsWith('#hub-section-') ? location.hash.slice(1) : null;
      if (current) {
        const motion = document.documentElement.dataset.placeMotion;
        const continuing = motion === 'shallower' || motion === 'deeper' || motion === 'replace';
        const poppedReturn = alreadyStarted && navigationType === 'POP' && programScanWasSeen(slug);
        if (!continuing && !poppedReturn) markProgramScanUnseen(slug);
        if (sectionId) {
          markPlaceFocus(sectionHeading(sectionId));
        } else {
          window.scrollTo(0, 0);
          markPlaceFocus(document.getElementById('entered-hub-title'));
        }
      } else if (wantsProgramScanRestore(location.state)) {
        window.scrollTo(0, readProgramScan(slug));
        markPlaceFocus(chapterHeading(readFocusHubSlug(location.state)));
      }
      return;
    }
    if (previous && !current) {
      window.scrollTo(0, programScanWasSeen(slug) ? readProgramScan(slug) : 0);
      markPlaceFocus(chapterHeading(readFocusHubSlug(location.state) ?? previous));
      return;
    }
    if (!previous && current) {
      const sectionId = location.hash.startsWith('#hub-section-') ? location.hash.slice(1) : null;
      if (sectionId) {
        markPlaceFocus(sectionHeading(sectionId));
      } else {
        markPlaceFocus(document.getElementById('entered-hub-title'));
        window.scrollTo(0, 0);
      }
    }
  }, [focus.hubSlug, slug, program, location.hash, location.state, navigationType]);

  const sections = useMemo(
    () => [...(program?.sections ?? [])].sort((a, b) => a.order - b.order),
    [program]
  );
  const highlights = useMemo(
    () => [...(program?.highlights ?? [])].sort((a, b) => a.order - b.order),
    [program]
  );
  const hubs = useMemo(
    () =>
      [...(program?.hubs ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title)
      ),
    [program]
  );
  const previewPostIds = useMemo(() => {
    const ids = new Set<number>();
    for (const hub of hubs) {
      if (hubOutline(hub.sections, hub.resources).length > 0) continue;
      for (const resource of previewResources(hub.resources)) ids.add(resource.wpPostId);
    }
    return ids;
  }, [hubs]);

  const topSections = useMemo(
    () => sections.filter((section) => !section.parentId),
    [sections]
  );
  const anchors = useMemo(
    () => sectionAnchors([...sections].sort((a, b) => a.order - b.order || a.title.localeCompare(b.title))),
    [sections]
  );
  const sectionChildren = (parentId: string) =>
    sections.filter((section) => section.parentId === parentId).sort((a, b) => a.order - b.order);
  const tocSectionIds = useMemo(() => {
    if (!program) return [];
    if (focus.hubSlug) {
      const hub = hubs.find((item) => item.slug === focus.hubSlug);
      return hub
        ? toolkitNavItems(
            hubOutlineTree(hubOutline(hub.sections, hub.resources)),
            (node) => `hub-section-${node.id}`,
            null,
            false
          ).flatMap(function ids(item): string[] {
            return [item.id, ...(item.children ?? []).flatMap(ids)];
          })
        : [];
    }
    const ids = hubs.map((hub) => `hub-${hub.slug}`);
    if (highlights.some((highlight) => !highlight.sectionId)) ids.push('highlighted');
    const walk = (section: (typeof sections)[number]): string[] => {
      const id = anchors.get(section.id);
      if (!id) return [];
      if (section.slug) return [id];
      const children = sections
        .filter((item) => item.parentId === section.id)
        .sort((a, b) => a.order - b.order);
      return [id, ...children.flatMap(walk)];
    };
    ids.push(...topSections.flatMap(walk));
    return ids;
  }, [program, hubs, sections, highlights, focus.hubSlug, topSections, anchors]);

  const activeSectionId = useScrollSpy(tocSectionIds);

  if (loading) {
    return (
      <div ref={pageRef} data-place-page className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white">
        <LoadingSkeleton lines={10} className="mb-4" />
        <LoadingSkeleton lines={5} />
      </div>
    );
  }

  if (error || !program || (location.pathname.startsWith('/references/') ? program.kind !== 'REFERENCE' : program.kind === 'REFERENCE')) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white">
        <ErrorState
          error={error || new Error('Not found')}
          title="Not found"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  const hubCountLabel = hubs.length === 0 ? '' : `${hubs.length} ${hubs.length === 1 ? 'toolkit' : 'toolkits'}`;
  const pageQuery = pageFilter.trim().toLowerCase();
  const includesQuery = (value: string) => !pageQuery || value.toLowerCase().includes(pageQuery);
  const documentTitle = (post: WPPost) => decodeHtmlEntities(post.title.rendered);
  const postsInSection = (sectionId: string) =>
    highlights
      .filter((highlight) => highlight.sectionId === sectionId)
      .map((highlight) => resourcePosts.get(highlight.wpPostId))
      .filter((post): post is WPPost => Boolean(post));
  const sectionMatches = (node: (typeof sections)[number]): boolean => {
    if (!pageQuery) return true;
    if (includesQuery(node.title)) return true;
    if (postsInSection(node.id).some((post) => includesQuery(documentTitle(post)))) return true;
    return sectionChildren(node.id).some(sectionMatches);
  };
  const hubMatches = (hub: (typeof hubs)[number]) => {
    if (!pageQuery) return true;
    if (includesQuery(hub.title)) return true;
    return hub.sections.some((section) => !section.title.includes(' / ') && includesQuery(section.title));
  };
  const enteredHub = focus.hubSlug ? hubs.find((hub) => hub.slug === focus.hubSlug) ?? null : null;
  const enteredOutline = enteredHub ? hubOutline(enteredHub.sections, enteredHub.resources) : [];
  const navItems: InThisNavItem[] = enteredHub
    ? toolkitNavItems(
        hubOutlineTree(enteredOutline),
        (node) => `hub-section-${node.id}`,
        activeSectionId,
        false
      )
    : [
        ...hubs.filter((hub) => hubMatches(hub)).map((hub) => ({
          id: `hub-${hub.slug}`,
          label: hub.title,
          current: activeSectionId === `hub-${hub.slug}`,
        })),
        ...(highlights.some((highlight) => {
          if (highlight.sectionId) return false;
          if (!pageQuery) return true;
          const post = resourcePosts.get(highlight.wpPostId);
          return Boolean(post && includesQuery(documentTitle(post)));
        })
          ? [{
              id: 'highlighted',
              label: 'Highlighted',
              current: activeSectionId === 'highlighted',
            }]
          : []),
        ...topSections.filter((section) => sectionMatches(section)).map((section) => {
          const item = (node: (typeof sections)[number], respectFilter: boolean): InThisNavItem | null => {
            if (respectFilter && !sectionMatches(node)) return null;
            const id = anchors.get(node.id) ?? node.id;
            const showAllChildren = !respectFilter || includesQuery(node.title);
            const children = node.slug
              ? []
              : sectionChildren(node.id)
                .map((child) => item(child, !showAllChildren))
                .filter((child): child is InThisNavItem => child !== null);
            return {
              id,
              label: node.title,
              current: activeSectionId === id,
              children: children.length > 0 ? children : undefined,
            };
          };
          return item(section, true);
        }).filter((item): item is InThisNavItem => item !== null),
      ];
  const showNav = navItems.length > 0;

  const leaveHub = () => {
    const next = new URLSearchParams(location.search);
    next.delete('title');
    const search = next.toString();
    beginPlaceTransition('shallower', () => {
      navigate(
        { pathname: placePath(program), search: search ? `?${search}` : '' },
        { preventScrollReset: true, state: location.state }
      );
    });
  };

  const selectInProgram = (id: string) => {
    navigate({ hash: id }, { preventScrollReset: true, state: withSmoothScroll(location.state) });
  };

  const highlightedItems = highlights
    .filter((highlight) => !highlight.sectionId)
    .filter((highlight) => !previewPostIds.has(highlight.wpPostId));
  const highlightedPosts = highlightedItems
    .map((highlight) => {
      const post = resourcePosts.get(highlight.wpPostId);
      return post ? { highlight, post } : null;
    })
    .filter((item): item is { highlight: ProgramHighlight; post: WPPost } => item !== null);
  const showHighlighted =
    highlightedItems.length > 0 &&
    (!pageQuery || highlightedPosts.some(({ post }) => includesQuery(documentTitle(post))));

  return (
    <div ref={pageRef} data-place-page className="bg-white">
      <div className="place-room">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Breadcrumb
          items={
            enteredHub
              ? [
                  { label: 'Home', to: '/', step: 'leave' },
                  { label: 'Resources', to: '/resources', step: 'leave' },
                  {
                    label: program.title,
                    to: placePath(program),
                    step: 'shallower',
                  },
                  { label: enteredHub.title },
                ]
              : [
                  { label: 'Home', to: '/', step: 'leave' },
                  { label: 'Resources', to: '/resources', step: 'leave' },
                  { label: program.title },
                ]
          }
        />
      </div>

      <RoomBar
        title={program.title}
        to={enteredHub ? placePath(program) : undefined}
        openBottom={!enteredHub}
        onNavigate={
          enteredHub
            ? (event) => {
                if (!isPlainClick(event)) return;
                event.preventDefault();
                leaveHub();
              }
            : undefined
        }
      />
      </div>

      <div className={`place-content ${placeEnterClass(slide)}`}>
      {!enteredHub ? (
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8">
            <p className="text-sm text-gray-600">{hubCountLabel}</p>
            {program.introduction ? (
              <p className="mt-3 text-lg text-gray-600 max-w-3xl">{program.introduction}</p>
            ) : null}
            <div className="mt-4 max-w-xl">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <Input
                  type="search"
                  placeholder="Filter by title"
                  value={pageFilter}
                  onChange={(event) => setPageFilter(event.target.value)}
                  className="pl-10"
                  aria-label={`Filter ${program.title} by title`}
                />
              </div>
              {pageQuery ? (
                <p className="mt-3 text-sm text-gray-600">
                  <Link
                    to={`/search?q=${encodeURIComponent(pageFilter.trim())}`}
                    className="font-medium text-primary hover:text-primary-dark"
                  >
                    Search all resources
                  </Link>
                </p>
              ) : null}
            </div>
            <PlaceExtras slug={program.slug} />
          </div>
        </div>
      ) : null}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {enteredHub ? (
          <button
            type="button"
            onClick={leaveHub}
            className="inline-flex min-h-[44px] items-center gap-2 text-primary hover:text-primary-dark mb-8 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back to {program.title}</span>
          </button>
        ) : (
          <Link
            to={listBackTo}
            onClick={(event) => closePlace(event, listBackTo, navigate, openedSurface, reopenSearchState(entrance))}
            className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-8 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>{listBackLabel}</span>
          </Link>
        )}
        <div className={showNav ? PAGE_SIDEBAR_LAYOUT : undefined}>
          {showNav ? (
            <PageSidebar>
              <InThisNav
                label={enteredHub ? 'In this toolkit' : 'On this page'}
                items={navItems}
                onSelect={selectInProgram}
              />
            </PageSidebar>
          ) : null}

          <div className="min-w-0 space-y-12">
            {(enteredHub ? [enteredHub] : hubs.filter((hub) => hubMatches(hub))).map((hub) => (
              <ProgramHubChapter
                key={hub.id}
                hub={hub}
                entered={hub.slug === focus.hubSlug}
                resourcePosts={resourcePosts}
                attachmentsByPostId={attachmentsByPostId}
                failedPostIds={failedPostIds}
                postsReady={postsReady}
                programSlug={program.slug}
                titleQuery={pageQuery}
                room={{
                  title: program.title,
                  to: placePath(program),
                  backTo: programHubHref(program.slug, hub.slug),
                  backLabel: `Back to ${hub.title}`,
                  crumbs: [
                    { label: 'Home', to: '/', step: 'leave' },
                    { label: 'Resources', to: '/resources', step: 'leave' },
                    { label: program.title, to: placePath(program), step: 'shallower' },
                    {
                      label: hub.title,
                      to: programHubHref(program.slug, hub.slug),
                      step: 'shallower',
                    },
                  ],
                }}
              />
            ))}

            {!enteredHub && showHighlighted && (
              <section id="highlighted" className="scroll-mt-24">
                <div className="flex items-center gap-2 mb-5">
                  <StarIcon className="h-5 w-5 text-amber-500" aria-hidden />
                  <h2 className="text-2xl font-semibold text-gray-900">Highlighted resources</h2>
                </div>
                <div className="space-y-4">
                  {highlightedItems.map((highlight) => {
                    const post = resourcePosts.get(highlight.wpPostId);
                    if (!post) {
                      if (pageQuery || postsReady || failedPostIds.has(highlight.wpPostId)) return null;
                      return <ResourceCardSkeleton key={highlight.id} />;
                    }
                    if (!includesQuery(documentTitle(post))) return null;
                    const hub = hubForHighlight(hubs, post.id);
                    return (
                    <ResourceCard
                      key={highlight.id}
                      post={post}
                      view="list"
                      fromHubSlug={hub?.slug}
                      originVia="highlight"
                      returnToProgram={Boolean(hub)}
                      room={
                        hub
                          ? {
                              title: program.title,
                              to: placePath(program),
                              backTo: programHubHref(program.slug, hub.slug),
                              backLabel: `Back to ${hub.title}`,
                              crumbs: [
                                { label: 'Home', to: '/', step: 'leave' },
                                { label: 'Resources', to: '/resources', step: 'leave' },
                                {
                                  label: program.title,
                                  to: placePath(program),
                                  step: 'shallower',
                                },
                                {
                                  label: hub.title,
                                  to: programHubHref(program.slug, hub.slug),
                                  step: 'shallower',
                                },
                              ],
                            }
                          : undefined
                      }
                      attachments={attachmentsByPostId.get(post.id) ?? []}
                      summarizeAttachments
                    />
                    );
                  })}
                </div>
              </section>
            )}

            {!enteredHub && topSections.filter((section) => sectionMatches(section)).map((section) => {
              const highlightsFor = (sectionId: string) =>
                highlights.filter((highlight) => highlight.sectionId === sectionId);
              const renderSection = (node: (typeof sections)[number], level: number, force = false) => {
                if (!force && !sectionMatches(node)) return null;
                const showAll = force || !pageQuery || includesQuery(node.title);
                const id = anchors.get(node.id) ?? node.id;
                const Heading = level === 0 ? 'h2' : 'h3';
                if (node.slug) {
                  return (
                    <section
                      key={node.id}
                      id={id}
                      className="scroll-mt-24"
                    >
                      <h2
                        className={
                          level === 0
                            ? `text-2xl font-bold text-gray-900 ${stickySectionHeadingClass(0)}`
                            : `text-xl font-semibold text-gray-900 ${stickySectionHeadingClass(level)}`
                        }
                      >
                        <Link to={`${placePath(program)}/${node.slug}`} className="hover:text-primary">
                          {node.title}
                        </Link>
                      </h2>
                    </section>
                  );
                }
                const sectionHighlights = highlightsFor(node.id);
                const childNodes = sectionChildren(node.id)
                  .filter((child) => showAll || sectionMatches(child))
                  .map((child) => renderSection(child, level + 1, showAll));
                const hasNest = childNodes.length > 0;
                const titleClass = level === 0 ? 'text-2xl font-bold text-gray-900' : 'text-xl font-semibold text-gray-900';
                const body = node.body ? (
                  <div
                    className={`${hubProseClassName} [&>*:first-child]:mt-0 [&>*:last-child]:mb-0`}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(node.body, { ADD_ATTR: ['target'] }),
                    }}
                  />
                ) : null;
                const cardNodes = sectionHighlights
                  .map((highlight) => {
                    const post = resourcePosts.get(highlight.wpPostId);
                    if (post) {
                      if (!(showAll || includesQuery(documentTitle(post)))) return null;
                      return <ResourceCard key={post.id} post={post} view="list" />;
                    }
                    if (pageQuery || postsReady || failedPostIds.has(highlight.wpPostId)) return null;
                    return <ResourceCardSkeleton key={highlight.id} />;
                  })
                  .filter(Boolean);
                const cards =
                  cardNodes.length > 0 ? <div className="space-y-4">{cardNodes}</div> : null;
                return (
                  <section
                    key={node.id}
                    id={id}
                    className="scroll-mt-24"
                  >
                    <StickySectionHeading
                      level={level}
                      as={Heading}
                      nested={hasNest}
                      className={titleClass}
                    >
                      {node.title}
                    </StickySectionHeading>
                    {hasNest ? (
                      <NestedSectionRail>
                        {body}
                        {cards}
                        {childNodes}
                      </NestedSectionRail>
                    ) : (
                      <div className="mt-3 space-y-4">
                        {body}
                        {cards}
                      </div>
                    )}
                  </section>
                );
              };
              return renderSection(section, 0);
            })}

            {!enteredHub && pageQuery && !topSections.some((section) => sectionMatches(section)) && !hubs.some((hub) => hubMatches(hub)) && highlightedPosts.every(({ post }) => !includesQuery(documentTitle(post))) ? (
              <p className="text-gray-600">Nothing on this page matches that title.</p>
            ) : null}

            {!enteredHub && sections.length === 0 && hubs.length === 0 && highlights.length === 0 && (
              <div className="text-center py-16 text-gray-500 border border-dashed border-gray-200 rounded-lg">
                <p className="mb-2 font-medium text-gray-700">No documents in this place yet</p>
                <p className="text-sm">Sections, hubs, and highlighted resources will appear here once they are added.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

function PlaceExtras({ slug }: { slug: string }) {
  const { canViewData } = useAccessLevel();
  const order = slug === 'bloody-easy';
  const data =
    slug === 'massive-hemorrhage-protocol'
      ? { to: '/data/mhp-quality-metrics', label: 'MHP quality metrics' }
      : slug.includes('bedside')
        ? { to: '/data/bedside-audit-of-blood-administration', label: 'Bedside audit results' }
        : null;
  if (!order && !(data && canViewData)) return null;
  return (
    <p className="mt-3 text-sm text-gray-700">
      {order && (
        <Link to="/order-resources" className="font-medium text-primary hover:text-primary-dark">
          Order the printed handbook
        </Link>
      )}
      {order && data && canViewData ? <span> · </span> : null}
      {data && canViewData && (
        <Link to={data.to} className="font-medium text-primary hover:text-primary-dark">
          {data.label}
        </Link>
      )}
    </p>
  );
}
